/**
 * Market insights service (comps/AVM/trends).
 *
 * Default path (USE_MOCKS=false): real apiClient call into
 * `GET /properties/{propertyId}/market-insights`, wired in
 * `infra/apigateway-market-insights.ts`.
 *
 * Mock path (USE_MOCKS=true): static fixture from ./mocks.ts, for local
 * design work without a deployed backend / real ATTOM key.
 *
 * Never throws on a component being unavailable — that's an expected,
 * per-component state (see MarketInsightsResponse), not an error. The view
 * renders each of avm/comparables/trends independently based on its own
 * `status`.
 */
import { MOCK_MARKET_INSIGHTS } from './mocks';

import type { MarketInsightsResponse } from '@/models/MarketInsights';

import { apiClient } from '@/services/api';
import { logger } from '@/utils/logger/loggerInstance';

const USE_MOCKS = false;

export const getMarketInsights = async (
  propertyId?: string,
): Promise<MarketInsightsResponse | null> => {
  if (!propertyId) return null;

  if (USE_MOCKS) {
    logger.debug(`[mock] GET /properties/${propertyId}/market-insights`);
    return MOCK_MARKET_INSIGHTS;
  }

  const url = `/properties/${propertyId}/market-insights`;
  logger.debug(`GET ${url}`);
  const response = await apiClient.get<MarketInsightsResponse>(url);
  return response.data;
};

export const setCompStarred = async ({
  propertyId,
  attomId,
  starred,
}: {
  propertyId: string;
  attomId: string;
  starred: boolean;
}): Promise<{ attomId: string; starred: boolean }> => {
  if (USE_MOCKS) {
    logger.debug(`[mock] PUT /properties/${propertyId}/market-insights/comps/${attomId}/star`, {
      starred,
    });
    return { attomId, starred };
  }

  const url = `/properties/${propertyId}/market-insights/comps/${attomId}/star`;
  logger.debug(`PUT ${url}`);
  const response = await apiClient.put<{ attomId: string; starred: boolean }>(url, { starred });
  return response.data;
};
